const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Search,
  X,
  Loader,
  Info,
  ArrowLeft,
  Image as ImageIcon,
  RefreshCw,
  Building,
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const PaymentProofUpload = () => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [transactionRef, setTransactionRef] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [loading, setLoading] = useState(true); // Start as true
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userId, setUserId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Bank details for display
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [loadingBanks, setLoadingBanks] = useState(false);

  // FIXED: Get user ID and verify authentication on mount with proper async handling
  useEffect(() => {
    const checkAuthAndLoad = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try to get user from localStorage (multiple possible keys)
        let user = null;
        let userIdValue = null;

        // Check sparkingUser first
        const sparklingUserStr = localStorage.getItem('sparklingUser');
        if (sparklingUserStr) {
          user = JSON.parse(sparklingUserStr);
        }

        // Fallback to 'user'
        if (!user) {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            user = JSON.parse(userStr);
          }
        }

        if (user && user.id) {
          userIdValue = user.id;
          setUserId(userIdValue);
          setIsAuthenticated(true);
        } else {
          // Try to get userId from localStorage
          const storedUserId = localStorage.getItem('userId');
          if (storedUserId) {
            userIdValue = parseInt(storedUserId);
            setUserId(userIdValue);
            setIsAuthenticated(true);
          }
        }

        // If still no user, try to get from token
        if (!userIdValue) {
          const token = localStorage.getItem('sparklingToken');
          if (token) {
            try {
              const response = await fetch(`${API_BASE_URL}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const data = await response.json();
              if (data.success && data.user && data.user.id) {
                userIdValue = data.user.id;
                setUserId(userIdValue);
                setIsAuthenticated(true);
                // Save user to localStorage
                localStorage.setItem('sparklingUser', JSON.stringify(data.user));
              }
            } catch (err) {}
          }
        }

        // If authenticated, fetch orders and bank details
        if (userIdValue) {
          await Promise.all([fetchPendingOrders(userIdValue), fetchBankDetails()]);
        } else {
          setIsAuthenticated(false);
        }
      } catch (err) {
        setError('Failed to load user information. Please refresh the page.');
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndLoad();
  }, []);

  const fetchPendingOrders = async (uid) => {
    if (!uid) return;

    try {
      const url = `${API_BASE_URL}/orders/get-pending-proof?user_id=${uid}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('sparklingToken')}` },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setPendingOrders(data.orders || []);
      } else {
        setError(data.error || 'Failed to fetch orders');
      }
    } catch (err) {
      setError('Connection error. Please check your internet and try again.');
    }
  };

  const fetchBankDetails = async () => {
    setLoadingBanks(true);
    try {
      const response = await fetch(`${API_BASE_URL}/bank-details/get`);
      const data = await response.json();
      if (data.success) {
        setBankDetails(data.banks || []);
        // Select default bank if available
        const defaultBank =
          _optionalChain([
            data,
            'access',
            (_3) => _3.banks,
            'optionalAccess',
            (_4) => _4.find,
            'call',
            (_5) => _5((b) => b.is_default),
          ]) || _optionalChain([data, 'access', (_6) => _6.banks, 'optionalAccess', (_7) => _7[0]]);
        setSelectedBank(defaultBank || null);
      }
    } catch (error) {
    } finally {
      setLoadingBanks(false);
    }
  };

  const handleFileChange = (e) => {
    const file = _optionalChain([
      e,
      'access',
      (_8) => _8.target,
      'access',
      (_9) => _9.files,
      'optionalAccess',
      (_10) => _10[0],
    ]);
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (JPG, PNG)');
        return;
      }

      setProofFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedOrder) {
      setError('Please select an order');
      return;
    }

    if (!proofFile) {
      setError('Please upload proof of payment');
      return;
    }

    if (!transactionRef.trim()) {
      setError('Please enter transaction reference number');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(30);

    try {
      const proofBase64 = await fileToBase64(proofFile);

      setUploadProgress(60);

      const payload = {
        order_id: selectedOrder.id,
        payment_proof: proofBase64,
        transaction_id: transactionRef,
        user_id: userId,
      };

      const response = await fetch(`${API_BASE_URL}/orders/upload-proof`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify(payload),
      });

      setUploadProgress(80);

      const responseText = await response.text();

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error(`Server returned invalid response: ${responseText.substring(0, 100)}`);
      }

      setUploadProgress(100);

      if (data.success) {
        setSuccess(
          'Payment proof uploaded successfully! Our team will verify your payment within 24 hours.'
        );
        setSelectedOrder(null);
        setProofFile(null);
        setProofPreview(null);
        setTransactionRef('');
        setSearchTerm('');

        // Refresh the list
        if (userId) {
          await fetchPendingOrders(userId);
        }

        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.error || 'Failed to upload proof');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection error. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const clearSelection = () => {
    setSelectedOrder(null);
    setProofFile(null);
    setProofPreview(null);
    setTransactionRef('');
    setError(null);
  };

  const filteredOrders = pendingOrders.filter(
    (order) =>
      order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.transaction_id &&
        order.transaction_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Show loading spinner while checking authentication
  if (loading) {
    return React.createElement(
      'div',
      {
        className: 'min-h-screen bg-stone-50 pt-32 pb-20',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 350 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-4xl mx-auto px-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 351 },
        },
        React.createElement(
          'div',
          {
            className: 'flex justify-center items-center h-64',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 352 },
          },
          React.createElement(
            'div',
            {
              className: 'text-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 353 },
            },
            React.createElement(Loader, {
              size: 40,
              className: 'animate-spin text-gold-600 mx-auto mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 354 },
            }),
            React.createElement(
              'p',
              {
                className: 'text-stone-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 355 },
              },
              'Loading your account...'
            )
          )
        )
      )
    );
  }

  // If not authenticated, show login message with back button
  if (!isAuthenticated) {
    return React.createElement(
      'div',
      {
        className: 'min-h-screen bg-stone-50 pt-32 pb-20',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 366 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-4xl mx-auto px-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 367 },
        },
        React.createElement(
          'button',
          {
            onClick: () => window.history.back(),
            className:
              'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-6 transition-colors',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 368 },
          },

          React.createElement(ArrowLeft, {
            size: 14,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 372 },
          }),
          ' Back'
        ),

        React.createElement(
          'div',
          {
            className: 'bg-white border border-stone-200 rounded-sm shadow-lg p-12 text-center',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 375 },
          },
          React.createElement(AlertCircle, {
            size: 48,
            className: 'mx-auto text-amber-500 mb-4',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 376 },
          }),
          React.createElement(
            'h2',
            {
              className: 'font-serif text-2xl text-stone-900 mb-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 377 },
            },
            'Authentication Required'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-600 mb-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 378 },
            },
            'Please log in to view your orders and upload payment proof.'
          ),
          React.createElement(
            'div',
            {
              className: 'flex gap-4 justify-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 379 },
            },
            React.createElement(
              'button',
              {
                onClick: () => (window.location.href = '/auth'),
                className:
                  'bg-gold-600 text-white px-8 py-3 rounded-sm hover:bg-gold-500 transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 380 },
              },
              'Go to Login'
            ),
            React.createElement(
              'button',
              {
                onClick: () => window.location.reload(),
                className:
                  'border border-stone-300 text-stone-600 px-8 py-3 rounded-sm hover:bg-stone-50 transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 386 },
              },
              'Try Again'
            )
          )
        )
      )
    );
  }

  return React.createElement(
    'div',
    {
      className: 'min-h-screen bg-stone-50 pt-32 pb-20',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 400 },
    },
    React.createElement(
      'div',
      {
        className: 'max-w-4xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 401 },
      },
      /* Back button */
      React.createElement(
        'button',
        {
          onClick: () => window.history.back(),
          className:
            'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-6 transition-colors',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 403 },
        },

        React.createElement(ArrowLeft, {
          size: 14,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 407 },
        }),
        ' Back'
      ),

      React.createElement(
        'h1',
        {
          className: 'font-serif text-3xl text-stone-900 mb-2',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 410 },
        },
        'Payment Verification'
      ),
      React.createElement(
        'p',
        {
          className: 'text-stone-600 mb-8',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 411 },
        },
        'Upload screenshots of bank transfers for manual verification'
      ),

      /* Upload Progress Bar */
      uploading &&
        React.createElement(
          'div',
          {
            className: 'mb-6',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 415 },
          },
          React.createElement(
            'div',
            {
              className: 'flex justify-between mb-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 416 },
            },
            React.createElement(
              'span',
              {
                className: 'text-xs text-stone-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 417 },
              },
              'Uploading...'
            ),
            React.createElement(
              'span',
              {
                className: 'text-xs text-stone-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 418 },
              },
              uploadProgress,
              '%'
            )
          ),
          React.createElement(
            'div',
            {
              className: 'w-full bg-stone-200 rounded-full h-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 420 },
            },
            React.createElement('div', {
              className: 'bg-gold-600 h-2 rounded-full transition-all duration-300',
              style: { width: `${uploadProgress}%` },
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 421 },
            })
          )
        ),

      error &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded-sm flex items-start gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 437 },
          },
          React.createElement(AlertCircle, {
            size: 16,
            className: 'flex-shrink-0 mt-0.5',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 438 },
          }),
          React.createElement(
            'span',
            {
              className: 'flex-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 439 },
            },
            error
          ),
          React.createElement(
            'button',
            {
              onClick: () => setError(null),
              className: 'ml-auto text-red-500 hover:text-red-700',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 440 },
            },
            React.createElement(X, {
              size: 16,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 441 },
            })
          )
        ),

      success &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 text-sm rounded-sm flex items-start gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 447 },
          },
          React.createElement(CheckCircle, {
            size: 16,
            className: 'flex-shrink-0 mt-0.5',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 448 },
          }),
          React.createElement(
            'span',
            {
              className: 'flex-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 449 },
            },
            success
          ),
          React.createElement(
            'button',
            {
              onClick: () => setSuccess(null),
              className: 'ml-auto text-green-500 hover:text-green-700',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 450 },
            },
            React.createElement(X, {
              size: 16,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 451 },
            })
          )
        ),

      React.createElement(
        'div',
        {
          className: 'bg-white border border-stone-200 rounded-sm shadow-lg overflow-hidden',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 456 },
        },
        /* Bank Details Summary */
        selectedBank &&
          React.createElement(
            'div',
            {
              className: 'p-6 border-b border-stone-200 bg-blue-50',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 459 },
            },
            React.createElement(
              'div',
              {
                className: 'flex items-center gap-2 mb-3',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 460 },
              },
              React.createElement(Building, {
                size: 18,
                className: 'text-gold-600',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 461 },
              }),
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-lg text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 462 },
                },
                'Bank Transfer Details'
              )
            ),
            React.createElement(
              'div',
              {
                className: 'grid grid-cols-1 md:grid-cols-2 gap-4 text-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 464 },
              },
              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 465 } },
                React.createElement(
                  'p',
                  {
                    className: 'text-xs text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 466 },
                  },
                  'Bank'
                ),
                React.createElement(
                  'p',
                  {
                    className: 'font-medium',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 467 },
                  },
                  selectedBank.bank_name
                ),
                selectedBank.branch &&
                  React.createElement(
                    'p',
                    {
                      className: 'text-xs text-stone-500',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 468 },
                    },
                    selectedBank.branch
                  )
              ),
              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 470 } },
                React.createElement(
                  'p',
                  {
                    className: 'text-xs text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 471 },
                  },
                  'Account Number'
                ),
                React.createElement(
                  'p',
                  {
                    className: 'font-mono font-medium',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 472 },
                  },
                  selectedBank.account_number
                )
              ),
              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 474 } },
                React.createElement(
                  'p',
                  {
                    className: 'text-xs text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 475 },
                  },
                  'Account Name'
                ),
                React.createElement(
                  'p',
                  {
                    className: 'font-medium',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 476 },
                  },
                  selectedBank.account_name
                )
              ),
              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 478 } },
                React.createElement(
                  'p',
                  {
                    className: 'text-xs text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 479 },
                  },
                  'Currency'
                ),
                React.createElement(
                  'p',
                  {
                    className: 'font-medium',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 480 },
                  },
                  selectedBank.currency || 'PHP'
                )
              )
            ),
            selectedBank.instructions &&
              React.createElement(
                'div',
                {
                  className:
                    'mt-3 p-3 bg-white rounded-sm border border-blue-200 text-xs text-blue-800',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 484 },
                },
                selectedBank.instructions
              )
          ),

        /* Order Selection */
        React.createElement(
          'div',
          {
            className: 'p-6 border-b border-stone-200',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 492 },
          },
          React.createElement(
            'div',
            {
              className: 'flex justify-between items-center mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 493 },
            },
            React.createElement(
              'h2',
              {
                className: 'font-serif text-xl text-stone-900',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 494 },
              },
              'Select Order'
            ),
            selectedOrder &&
              React.createElement(
                'button',
                {
                  onClick: clearSelection,
                  className: 'text-sm text-gold-600 hover:text-gold-500 flex items-center gap-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 496 },
                },

                React.createElement(X, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 500 },
                }),
                ' Change Order'
              )
          ),

          pendingOrders.length === 0
            ? React.createElement(
                'div',
                {
                  className: 'text-center py-12 bg-stone-50 rounded-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 506 },
                },
                React.createElement(CheckCircle, {
                  size: 48,
                  className: 'mx-auto text-green-500 mb-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 507 },
                }),
                React.createElement(
                  'p',
                  {
                    className: 'text-stone-600 font-medium',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 508 },
                  },
                  'No pending orders found'
                ),
                React.createElement(
                  'p',
                  {
                    className: 'text-sm text-stone-400 mt-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 509 },
                  },
                  'All your orders have been verified'
                ),
                React.createElement(
                  'button',
                  {
                    onClick: () => userId && fetchPendingOrders(userId),
                    className:
                      'mt-4 text-gold-600 hover:text-gold-500 text-sm underline flex items-center gap-1 mx-auto',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 510 },
                  },

                  React.createElement(RefreshCw, {
                    size: 12,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 514 },
                  }),
                  ' Refresh'
                )
              )
            : React.createElement(
                React.Fragment,
                null,
                /* Search */
                React.createElement(
                  'div',
                  {
                    className: 'relative mb-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 520 },
                  },
                  React.createElement(Search, {
                    size: 16,
                    className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 521 },
                  }),
                  React.createElement('input', {
                    type: 'text',
                    placeholder: 'Search by order number...',
                    value: searchTerm,
                    onChange: (e) => setSearchTerm(e.target.value),
                    className:
                      'w-full pl-10 pr-4 py-2 border border-stone-200 rounded-sm focus:border-gold-600 focus:outline-none text-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 522 },
                  })
                ),

                /* Orders List */
                React.createElement(
                  'div',
                  {
                    className: 'space-y-2 max-h-96 overflow-y-auto pr-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 532 },
                  },
                  filteredOrders.length === 0
                    ? React.createElement(
                        'p',
                        {
                          className: 'text-center py-8 text-stone-400',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 534 },
                        },
                        'No orders match your search'
                      )
                    : filteredOrders.map((order) =>
                        React.createElement(
                          'label',
                          {
                            key: order.id,
                            className: `flex items-center p-4 border rounded-sm cursor-pointer transition-all ${
                              _optionalChain([selectedOrder, 'optionalAccess', (_11) => _11.id]) ===
                              order.id
                                ? 'border-gold-600 bg-gold-50 ring-1 ring-gold-600'
                                : 'border-stone-200 hover:border-gold-600 hover:bg-stone-50'
                            }`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 537 },
                          },

                          React.createElement('input', {
                            type: 'radio',
                            name: 'order',
                            value: order.id,
                            checked:
                              _optionalChain([selectedOrder, 'optionalAccess', (_12) => _12.id]) ===
                              order.id,
                            onChange: () => setSelectedOrder(order),
                            className: 'sr-only',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 545 },
                          }),
                          React.createElement(
                            'div',
                            {
                              className: 'flex-1 flex items-center justify-between',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 553 },
                            },
                            React.createElement(
                              'div',
                              {
                                className: 'flex-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 554 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className: 'font-medium text-stone-900',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 555 },
                                },
                                order.order_number
                              ),
                              React.createElement(
                                'p',
                                {
                                  className: 'text-xs text-stone-500',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 556 },
                                },
                                new Date(order.created_at).toLocaleDateString()
                              ),
                              order.transaction_id &&
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-xs text-stone-400 mt-1',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 558 },
                                  },
                                  'Ref: ',
                                  order.transaction_id
                                ),
                              order.status === 'Rejected' &&
                                order.rejection_reason &&
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-xs text-red-600 mt-1 font-medium',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 560 },
                                  },
                                  'Rejected: ',
                                  order.rejection_reason
                                )
                            ),
                            React.createElement(
                              'div',
                              {
                                className: 'text-right ml-4',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 561 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className: 'font-bold text-gold-600',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 562 },
                                },
                                '₱ ',
                                parseFloat(order.total_amount || 0).toLocaleString()
                              ),
                              order.status === 'Rejected'
                                ? React.createElement(
                                    'span',
                                    {
                                      className:
                                        'inline-block px-2 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded-full mt-1',
                                    },
                                    'Rejected'
                                  )
                                : React.createElement(
                                    'span',
                                    {
                                      className:
                                        'inline-block px-2 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 rounded-full mt-1',
                                    },
                                    'Pending'
                                  )
                            )
                          )
                        )
                      )
                )
              )
        ),

        /* Upload Form */
        selectedOrder &&
          React.createElement(
            'form',
            {
              onSubmit: handleSubmit,
              className: 'p-6 bg-stone-50',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 578 },
            },
            React.createElement(
              'h2',
              {
                className: 'font-serif text-xl text-stone-900 mb-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 579 },
              },
              'Upload Proof of Payment'
            ),

            /* Order Summary */
            React.createElement(
              'div',
              {
                className: 'mb-6 p-4 bg-white border border-stone-200 rounded-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 582 },
              },
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-500 mb-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 583 },
                },
                'Selected Order'
              ),
              React.createElement(
                'p',
                {
                  className: 'font-medium',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 584 },
                },
                selectedOrder.order_number
              ),
              React.createElement(
                'p',
                {
                  className: 'text-sm text-stone-600 mt-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 585 },
                },
                'Amount to Pay: ',
                React.createElement(
                  'span',
                  {
                    className: 'font-bold text-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 586 },
                  },
                  '₱ ',
                  parseFloat(selectedOrder.total_amount || 0).toLocaleString()
                )
              )
            ),

            /* Transaction Reference */
            React.createElement(
              'div',
              {
                className: 'mb-6',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 591 },
              },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 592 },
                },
                'Transaction Reference No. ',
                React.createElement(
                  'span',
                  {
                    className: 'text-red-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 593 },
                  },
                  '*'
                )
              ),
              React.createElement('input', {
                type: 'text',
                value: transactionRef,
                onChange: (e) => setTransactionRef(e.target.value),
                placeholder: 'e.g. TR-123456789',
                className:
                  'w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-600 focus:outline-none rounded-sm bg-white',
                required: true,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 595 },
              }),
              React.createElement(
                'p',
                {
                  className: 'text-[10px] text-stone-400 mt-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 603 },
                },
                'Enter the reference number from your bank transfer receipt'
              )
            ),

            /* File Upload */
            React.createElement(
              'div',
              {
                className: 'mb-6',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 607 },
              },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 608 },
                },
                'Upload Screenshot ',
                React.createElement(
                  'span',
                  {
                    className: 'text-red-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 609 },
                  },
                  '*'
                )
              ),

              proofPreview
                ? React.createElement(
                    'div',
                    {
                      className: 'mb-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 613 },
                    },
                    React.createElement(
                      'div',
                      {
                        className: 'relative inline-block',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 614 },
                      },
                      React.createElement('img', {
                        src: proofPreview,
                        alt: 'Proof preview',
                        className: 'h-40 w-auto rounded-sm border border-stone-200 shadow-sm',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 615 },
                      }),
                      React.createElement(
                        'button',
                        {
                          type: 'button',
                          onClick: () => {
                            setProofFile(null);
                            setProofPreview(null);
                          },
                          className:
                            'absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-md hover:bg-red-600 transition-colors',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 620 },
                        },

                        React.createElement(X, {
                          size: 14,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 628 },
                        })
                      )
                    )
                  )
                : null,

              React.createElement(
                'label',
                {
                  className:
                    'flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-sm cursor-pointer transition-all border-stone-300 hover:border-gold-600 hover:bg-white bg-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 634 },
                },
                React.createElement(
                  'div',
                  {
                    className: 'flex flex-col items-center justify-center pt-5 pb-6 text-stone-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 635 },
                  },
                  React.createElement(ImageIcon, {
                    className: 'w-8 h-8 mb-3',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 636 },
                  }),
                  React.createElement(
                    'p',
                    {
                      className: 'text-sm font-medium',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 637 },
                    },
                    'Click to upload screenshot'
                  ),
                  React.createElement(
                    'p',
                    {
                      className: 'text-xs mt-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 638 },
                    },
                    'JPG, PNG up to 5MB'
                  )
                ),
                React.createElement('input', {
                  type: 'file',
                  className: 'hidden',
                  accept: 'image/jpeg,image/png,image/jpg',
                  onChange: handleFileChange,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 640 },
                })
              )
            ),

            /* Submit Button */
            React.createElement(
              'button',
              {
                type: 'submit',
                disabled: uploading,
                className:
                  'w-full bg-gold-600 text-white py-4 uppercase text-xs tracking-[0.2em] hover:bg-gold-500 transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed rounded-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 650 },
              },

              uploading
                ? React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(Loader, {
                      size: 16,
                      className: 'animate-spin',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 657 },
                    }),
                    'Submitting... ',
                    uploadProgress,
                    '%'
                  )
                : 'Submit Verification'
            )
          )
      ),

      /* Instructions */
      React.createElement(
        'div',
        {
          className: 'mt-8 bg-blue-50 border border-blue-200 p-6 rounded-sm',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 669 },
        },
        React.createElement(
          'h3',
          {
            className: 'font-serif text-lg text-stone-900 mb-3 flex items-center gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 670 },
          },
          React.createElement(Info, {
            size: 18,
            className: 'text-gold-600',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 671 },
          }),
          'Payment Verification Process'
        ),
        React.createElement(
          'ol',
          {
            className: 'list-decimal list-inside space-y-2 text-sm text-stone-600',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 674 },
          },
          React.createElement(
            'li',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 675 } },
            "Select the order you've made a bank transfer for"
          ),
          React.createElement(
            'li',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 676 } },
            'Enter the transaction reference number from your bank receipt'
          ),
          React.createElement(
            'li',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 677 } },
            'Upload a clear screenshot or photo of your bank transfer receipt'
          ),
          React.createElement(
            'li',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 678 } },
            'Our concierge team will verify your payment within 24 hours'
          ),
          React.createElement(
            'li',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 679 } },
            "You'll receive an email confirmation once your payment is verified"
          )
        )
      )
    )
  );
};

export default PaymentProofUpload;
